import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, update } from 'firebase/database';
import { Briefcase, Mail, Phone, MapPin, Clock, Shield, Check, Star, Edit, Save, HardHat, X } from 'lucide-react';
import app from '../../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const db = getDatabase(app);

const PROFILE_IMAGES = [
  // Residential Specialists
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',

  // Commercial Focus
  'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1505236858219-8359eb29e329?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',

  // Mixed Specialization
  'https://images.unsplash.com/photo-1517502884422-41eaead166d4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1493809842364-78817add7ffb?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',

  // Luxury Focus
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',

  // Modern/Urban
  'https://images.unsplash.com/photo-1487958449943-2429e8be8625?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1505843513577-22bb7d21e455?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80',
  'https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80'
];

const SPECIALTY_OPTIONS = [
  'Residential', 'Commercial', 'Industrial', 'Renovation',
  'New Construction', 'Remodeling', 'Custom Homes', 'Additions'
];

const PRIMARY_SPECIALTY_OPTIONS = [
  'Residential', 'Commercial', 'Industrial'
];

const ContractorDashboard = () => {
  const [contractorData, setContractorData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    specialties: [],
    primarySpecialty: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [ratingStars, setRatingStars] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [showImageSelector, setShowImageSelector] = useState(false);

  useEffect(() => {
    const fetchContractorData = async () => {
      try {
        const contractorId = localStorage.getItem('contractorId');
        if (!contractorId) {
          throw new Error('Contractor ID not found');
        }

        // Fetch contractor data
        const contractorRef = ref(db, `Contractors/${contractorId}`);
        const contractorSnapshot = await get(contractorRef);

        if (contractorSnapshot.exists()) {
          const data = contractorSnapshot.val();
          
          // Convert specialties to array if it's an object
          let specialtiesArray = [];
          if (data.specialties) {
            if (Array.isArray(data.specialties)) {
              specialtiesArray = data.specialties;
            } else if (typeof data.specialties === 'object') {
              specialtiesArray = Object.values(data.specialties);
            }
          }

          setContractorData({
            ...data,
            specialties: specialtiesArray
          });
          
          setEditForm({
            ...data,
            profileImage: data.profileImage || PROFILE_IMAGES[0],
            specialties: specialtiesArray,
            primarySpecialty: data.primarySpecialty || ''
          });
          generateStars(data.rating || 0);

          // Fetch projects assigned to this contractor
          const projectsRef = ref(db, 'Projects');
          const projectsSnapshot = await get(projectsRef);

          if (projectsSnapshot.exists()) {
            const allProjects = projectsSnapshot.val();
            const contractorProjects = Object.entries(allProjects)
              .filter(([_, project]) => project.contractorId === contractorId)
              .map(([id, project]) => ({ id, ...project }));

            setProjects(contractorProjects);
          }
        } else {
          throw new Error('Contractor data not found');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContractorData();
  }, []);

  const generateStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="w-5 h-5 text-amber-400 fill-amber-400" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" className="w-5 h-5 text-amber-400 fill-amber-400 opacity-50" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-5 h-5 text-gray-200 fill-gray-200" />);
    }

    setRatingStars(stars);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpecialtyChange = (specialty) => {
    setEditForm(prev => {
      const currentSpecialties = Array.isArray(prev.specialties) ? prev.specialties : [];
      
      if (currentSpecialties.includes(specialty)) {
        return {
          ...prev,
          specialties: currentSpecialties.filter(s => s !== specialty)
        };
      } else {
        return {
          ...prev,
          specialties: [...currentSpecialties, specialty]
        };
      }
    });
  };

  const handlePrimarySpecialtyChange = (specialty) => {
    setEditForm(prev => ({
      ...prev,
      primarySpecialty: specialty
    }));
  };

  const handleImageSelect = (imageUrl) => {
    setEditForm(prev => ({
      ...prev,
      profileImage: imageUrl
    }));
    setShowImageSelector(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const contractorId = localStorage.getItem('contractorId');
      if (!contractorId) {
        throw new Error('Contractor ID not found');
      }

      const contractorRef = ref(db, `Contractors/${contractorId}`);
      await update(contractorRef, editForm);

      setContractorData(editForm);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating contractor data:', error);
      toast.error('Failed to update profile');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!contractorData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800">No contractor data found</h2>
          <p className="text-gray-600 mt-2">Please try logging in again</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer position="top-right" autoClose={3000} />

      {isEditing ? (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Edit Profile</h2>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-white bg-opacity-20 rounded-lg text-sm font-medium hover:bg-opacity-30 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-white text-orange-600 rounded-lg font-medium hover:bg-gray-50 flex items-center shadow-md transition-all"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Profile Image */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Image</h3>

                  <div className="relative group">
                    <div className="w-full h-64 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                      <img
                        src={editForm.profileImage || PROFILE_IMAGES[0]}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PROFILE_IMAGES[0];
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowImageSelector(true)}
                      className="absolute bottom-4 right-4 bg-orange-500 text-white p-2 rounded-full shadow-md hover:bg-orange-600 transition-all"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowImageSelector(true)}
                    className="mt-6 w-full py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Change Photo
                  </button>

                  {/* Image Selector Modal */}
                  {showImageSelector && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b">
                          <h3 className="text-lg font-medium">Select Profile Image</h3>
                          <button
                            onClick={() => setShowImageSelector(false)}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {PROFILE_IMAGES.map((img, index) => (
                              <div
                                key={index}
                                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${editForm.profileImage === img ? 'border-orange-500 ring-2 ring-orange-200' : 'border-transparent hover:border-gray-200'}`}
                                onClick={() => handleImageSelect(img)}
                              >
                                <div className="aspect-square">
                                  <img
                                    src={img}
                                    alt={`Profile ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                {editForm.profileImage === img && (
                                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                    <Check className="w-8 h-8 text-white" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="p-6 border-t">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Custom Image URL</label>
                          <div className="flex">
                            <input
                              type="url"
                              name="profileImage"
                              value={editForm.profileImage}
                              onChange={handleInputChange}
                              className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:ring-orange-500 focus:border-orange-500"
                              placeholder="https://example.com/image.jpg"
                            />
                            <button
                              type="button"
                              onClick={() => setShowImageSelector(false)}
                              className="px-4 bg-orange-500 text-white rounded-r-lg hover:bg-orange-600 transition-colors"
                            >
                              Apply
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">Enter a valid image URL to use a custom photo</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Verification Badges */}
                  <div className="mt-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Verification</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="insurance"
                          name="insurance"
                          checked={editForm.insurance || false}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label htmlFor="insurance" className="ml-2 text-sm text-gray-700">
                          Has Insurance
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="safetyCertified"
                          name="safetyCertified"
                          checked={editForm.safetyCertified || false}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label htmlFor="safetyCertified" className="ml-2 text-sm text-gray-700">
                          Safety Certified
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Form Fields */}
              <div className="lg:col-span-2">
                <div className="space-y-6">
                  {/* Company Information */}
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name*</label>
                        <input
                          type="text"
                          name="companyName"
                          value={editForm.companyName || ''}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">License Number*</label>
                        <input
                          type="text"
                          name="licenseNumber"
                          value={editForm.licenseNumber || ''}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contractor Type*</label>
                        <select
                          name="contractorType"
                          value={editForm.contractorType || ''}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="General Contractor">General Contractor</option>
                          <option value="Electrical">Electrical</option>
                          <option value="Plumbing">Plumbing</option>
                          <option value="HVAC">HVAC</option>
                          <option value="Roofing">Roofing</option>
                          <option value="Carpentry">Carpentry</option>
                          <option value="Masonry">Masonry</option>
                          <option value="Landscaping">Landscaping</option>
                          <option value="Painting">Painting</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Experience*</label>
                        <select
                          name="experience"
                          value={editForm.experience || ''}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        >
                          <option value="">Select experience</option>
                          <option value="0-2">0-2 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="6-10">6-10 years</option>
                          <option value="10+">10+ years</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Primary Specialty Section */}
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Primary Specialty</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {PRIMARY_SPECIALTY_OPTIONS.map(specialty => (
                        <div key={specialty} className="flex items-center">
                          <input
                            type="radio"
                            id={`primary-specialty-${specialty}`}
                            name="primarySpecialty"
                            checked={editForm.primarySpecialty === specialty}
                            onChange={() => handlePrimarySpecialtyChange(specialty)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                          />
                          <label htmlFor={`primary-specialty-${specialty}`} className="ml-2 text-sm text-gray-700">
                            {specialty}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Specialties Section */}
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Specialties</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {SPECIALTY_OPTIONS.map(specialty => (
                        <div key={specialty} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`specialty-${specialty}`}
                            checked={editForm.specialties?.includes(specialty) || false}
                            onChange={() => handleSpecialtyChange(specialty)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`specialty-${specialty}`} className="ml-2 text-sm text-gray-700">
                            {specialty}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Mail className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={editForm.email || ''}
                            onChange={handleInputChange}
                            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone*</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Phone className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="tel"
                            name="phone"
                            value={editForm.phone || ''}
                            onChange={handleInputChange}
                            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required
                          />
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address*</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="address"
                            value={editForm.address || ''}
                            onChange={handleInputChange}
                            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City*</label>
                        <input
                          type="text"
                          name="city"
                          value={editForm.city || ''}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State*</label>
                        <input
                          type="text"
                          name="state"
                          value={editForm.state || ''}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white relative">
            <div className="flex flex-col md:flex-row justify-between items-start">
              <div className="flex items-start space-x-6">
                <div className="w-24 h-24 rounded-lg border-2 border-white overflow-hidden shadow-lg">
                  <img
                    src={contractorData.profileImage || PROFILE_IMAGES[0]}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = PROFILE_IMAGES[0];
                    }}
                  />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{contractorData.companyName}</h1>
                  <p className="text-lg opacity-90 mt-1">
                    {contractorData.contractorType}
                    {contractorData.primarySpecialty && (
                      <span className="ml-2 px-2 py-1 bg-white bg-opacity-20 rounded-full text-sm">
                        {contractorData.primarySpecialty}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-4 md:mt-0 md:absolute md:right-6 md:top-6">
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-white text-orange-600 rounded-lg font-medium hover:bg-gray-100 flex items-center shadow-md transition-all"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 px-6">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'profile' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'projects' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                Projects ({projects.length})
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6 md:p-8">
            {activeTab === 'profile' ? (
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Main Profile Content */}
                <div className="lg:w-2/3">
                  {/* Rating and Basic Info */}
                  <div className="bg-gray-50 p-6 rounded-xl mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                      <div className="flex items-center mb-4 sm:mb-0">
                        <div className="flex mr-2">
                          {ratingStars}
                        </div>
                        <span className="text-gray-700 font-medium">
                          {contractorData.rating?.toFixed(1) || '4.9'} ({projects.length} projects)
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-5 h-5 mr-2 text-orange-500" />
                          <span>{contractorData.city}, {contractorData.state}</span>
                        </div>

                        <div className="flex items-center text-gray-600">
                          <Clock className="w-5 h-5 mr-2 text-orange-500" />
                          <span>{contractorData.experience} years experience</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* About Section */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">About</h2>
                    <div className="bg-gray-50 p-6 rounded-xl">
                      <p className="text-gray-700 leading-relaxed">
                        {contractorData.primarySpecialty && (
                          <span className="font-semibold">{contractorData.primarySpecialty} specialist</span>
                        )} specializing in {contractorData.specialties?.join(', ')?.toLowerCase() || 'various construction services'}.
                        Committed to quality workmanship and customer satisfaction with {contractorData.experience} years of experience
                        serving the {contractorData.city} area.
                      </p>
                    </div>
                  </div>

                  {/* Services Section */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Services Offered</h2>
                    <div className="bg-gray-50 p-6 rounded-xl">
                      <div className="flex flex-wrap gap-3">
                        {contractorData.primarySpecialty && (
                          <span className="px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-medium">
                            {contractorData.primarySpecialty}
                          </span>
                        )}
                        {contractorData.specialties?.map((specialty, index) => (
                          <span key={index} className="px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:w-1/3">
                  <div className="bg-gray-50 p-6 rounded-xl sticky top-6">
                    <h3 className="font-semibold text-lg mb-4 text-gray-800">Contact Information</h3>

                    <div className="space-y-5">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <Mail className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="text-gray-800 font-medium">{contractorData.email}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <Phone className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Phone</p>
                          <p className="text-gray-800 font-medium">{contractorData.phone}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <Briefcase className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">License Number</p>
                          <p className="text-gray-800 font-medium">{contractorData.licenseNumber}</p>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-700 mb-3">Certifications</h4>
                        <div className="space-y-3">
                          {contractorData.insurance && (
                            <div className="flex items-center text-green-700">
                              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2">
                                <Shield className="h-4 w-4" />
                              </div>
                              <span className="text-sm font-medium">Insured</span>
                            </div>
                          )}
                          {contractorData.safetyCertified && (
                            <div className="flex items-center text-green-700">
                              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2">
                                <Check className="h-4 w-4" />
                              </div>
                              <span className="text-sm font-medium">Safety Certified</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-6 text-gray-800">Assigned Projects</h2>
                {projects.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {projects.map(project => (
                      <div key={project.id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200">
                        <div className="h-48 bg-gray-100 overflow-hidden">
                          {project.projectImage ? (
                            <img
                              src={project.projectImage}
                              alt="Project"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/600x400?text=Project+Image';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                              <HardHat className="w-12 h-12 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="p-5">
                          <div className="flex justify-between items-start">
                            <h3 className="font-medium text-gray-900 text-lg">{project.projectName}</h3>
                            <span className={`px-3 py-1 text-xs rounded-full ${project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                project.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                              }`}>
                              {project.status || 'In Progress'}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 mt-2">
                            <MapPin className="inline w-4 h-4 mr-1 text-orange-500" />
                            {project.address}
                          </p>

                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-500">Start Date</p>
                                <p className="text-sm font-medium">{project.startDate || 'Not specified'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Budget</p>
                                <p className="text-sm font-medium">{project.budget ? `$${project.budget}` : 'Not specified'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <div className="mx-auto h-24 w-24 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                      <HardHat className="w-12 h-12 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No projects assigned</h3>
                    <p className="mt-2 text-gray-500 max-w-md mx-auto">
                      You haven't been assigned any projects yet. When you get assigned projects, they'll appear here.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorDashboard;